import { useEffect, useState } from 'react'
import BigButton from './BigButton'
import type { AdminRegistration } from '../lib/types'

type Props = {
  registration: AdminRegistration
  busy: boolean
  error: string | null
  onSave: (fields: {
    naam?: string
    email?: string
    gewenstAdres?: string
    aantalPlaatsen?: number
    notes?: string
    buurtbewoner?: boolean
  }) => void
  onDelete: () => void
  onSendMessage: () => void
  onCancel: () => void
}

export default function RegistrationEditRow({
  registration: r, busy, error,
  onSave, onDelete, onSendMessage, onCancel,
}: Props) {
  const [naam, setNaam] = useState(r.naam)
  const [email, setEmail] = useState(r.email)
  const [gewenstAdres, setGewenstAdres] = useState(r.gewenstAdres)
  const [aantalPlaatsen, setAantal] = useState(r.aantalPlaatsen)
  const [notes, setNotes] = useState(r.notes)
  const [buurtbewoner, setBuurtbewoner] = useState(r.buurtbewoner)

  useEffect(() => {
    setNaam(r.naam)
    setEmail(r.email)
    setGewenstAdres(r.gewenstAdres)
    setAantal(r.aantalPlaatsen)
    setNotes(r.notes)
    setBuurtbewoner(r.buurtbewoner)
  }, [r.id, r.naam, r.email, r.gewenstAdres, r.aantalPlaatsen, r.notes, r.buurtbewoner])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const fields: Parameters<Props['onSave']>[0] = {}
    if (naam !== r.naam) fields.naam = naam
    if (email !== r.email) fields.email = email
    if (gewenstAdres !== r.gewenstAdres) fields.gewenstAdres = gewenstAdres
    if (aantalPlaatsen !== r.aantalPlaatsen) fields.aantalPlaatsen = aantalPlaatsen
    if (notes !== r.notes) fields.notes = notes
    if (buurtbewoner !== r.buurtbewoner) fields.buurtbewoner = buurtbewoner
    onSave(fields)
  }

  function confirmDelete() {
    if (window.confirm(`${r.naam} verwijderen? Recupereerbaar via Notion's trash bin (30 dagen).`)) {
      onDelete()
    }
  }

  return (
    <form onSubmit={submit} className="bg-offwhite border-[3px] border-ink p-4 my-2">
      <div className="font-display tracking-display uppercase text-sm mb-3">
        Bewerken — {r.naam}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input className="input" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Naam" required />
        <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" type="email" />
        <input className="input" value={gewenstAdres} onChange={(e) => setGewenstAdres(e.target.value)} placeholder="Gewenst adres" required />
        <select className="input" value={aantalPlaatsen} onChange={(e) => setAantal(Number(e.target.value))}>
          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} plaats{n > 1 ? 'en' : ''}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 mb-3">
        <input type="checkbox" checked={buurtbewoner} onChange={(e) => setBuurtbewoner(e.target.checked)} />
        <span>Buurtbewoner</span>
      </label>
      <textarea
        className="input min-h-[5rem] mb-3"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes"
      />

      {error && (
        <div className="border-[3px] border-red bg-red/10 px-4 py-2 mb-3 text-sm text-red font-semibold">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <BigButton type="submit" disabled={busy}>
          {busy ? 'Bezig…' : 'Wijzigingen opslaan'}
        </BigButton>
        <button type="button" onClick={onCancel} className="text-sm underline text-muted">
          Annuleer
        </button>
        <button
          type="button"
          onClick={onSendMessage}
          className="text-sm underline ml-auto"
        >
          ✉ Persoonlijk bericht
        </button>
        <button
          type="button"
          onClick={confirmDelete}
          disabled={busy}
          className="text-sm underline text-red disabled:opacity-40"
        >
          ⌫ Verwijder inschrijving
        </button>
      </div>
    </form>
  )
}
